import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useState } from 'react';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

export type PlacementRenderProps = {
	onDismiss: () => void;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'statuspage-navigation-spotlight-placement',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	Record<string, never>
> = createFrontend<Record<string, never>>((Messages) => () => {
	const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage>;
	}>('/', {
		autoRefetch: {
			isEnabled: true,
			updateKey: 'statuspage-navigation-spotlight-placement',
		},
	});

	const messages = data?.messages;

	const visibleMessages = messages
		?.filter((message) => !dismissedIds.has(message.messageInstanceId))
		?.slice?.(0, 1);

	const handleDismiss = (messageInstanceId: string) => {
		setDismissedIds((prev) => new Set([...Array.from(prev), messageInstanceId]));
	};

	if (error) {
		throw error;
	}

	if (isLoading) {
		return null;
	}

	if (!visibleMessages || visibleMessages.length === 0) {
		return null;
	}

	return (
		<>
			{visibleMessages.map((message) => (
				<Messages.render
					key={message.messageInstanceId}
					onDismiss={() => handleDismiss(message.messageInstanceId)}
					{...message}
				/>
			))}
		</>
	);
});

export type PlacementFrontendProps = Record<string, never>;
